import { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import SectionHeader from '../../components/admin/SectionHeader';
import StatCard from '../../components/admin/StatCard';
import DataTable from '../../components/admin/DataTable';
import Badge from '../../components/admin/Badge';

const COLORS = ['#1aad9f', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

function AdminRevenue({ apiFetch, toast }) {
  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [trendMonths, setTrendMonths] = useState(12);
  const [billingFilter, setBillingFilter] = useState('all'); // 'all', 'monthly', 'yearly'

  const loadOverview = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/admin/revenue/overview?period=${period}`);
      if (res.ok) setOverview(await res.json());
      else toast.error('Error', 'Failed to load revenue overview');
    } catch { toast.error('Error', 'Failed to load revenue overview'); }
  }, [apiFetch, period, toast]);

  const loadTrend = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/admin/revenue/trend?months=${trendMonths}`);
      if (res.ok) setTrend(await res.json());
    } catch {}
  }, [apiFetch, trendMonths]);

  const loadInvoices = useCallback(async () => {
    try {
      const res = await apiFetch('/api/admin/revenue/recent-invoices?limit=20');
      if (res.ok) setInvoices(await res.json());
    } catch {}
  }, [apiFetch]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadOverview(), loadTrend(), loadInvoices()]).finally(() => setLoading(false));
  }, [loadOverview, loadTrend, loadInvoices]);

  const planData = overview?.planBreakdown
    ? Object.entries(overview.planBreakdown).map(([slug, data]) => {
        let value;
        if (billingFilter === 'monthly') value = data.monthly;
        else if (billingFilter === 'yearly') value = data.yearly / 12;
        else value = data.monthly + data.yearly / 12;
        return { name: slug, value: parseFloat(value.toFixed(2)), count: data.count };
      }).filter(p => p.value > 0).sort((a, b) => b.value - a.value)
    : [];

  const invoiceColumns = [
    { key: 'created', header: 'Date', width: '140px', render: row => new Date(row.created).toLocaleDateString() },
    { key: 'number', header: 'Invoice', width: '140px' },
    { key: 'customer_email', header: 'Customer' },
    { key: 'plan_slug', header: 'Plan', render: row => row.plan_slug ? <Badge variant="info">{row.plan_slug}</Badge> : '-' },
    { key: 'amount', header: 'Amount', width: '100px', cellStyle: { fontWeight: 600, color: '#1aad9f' }, render: row => `$${row.amount.toFixed(2)}` },
  ];

  if (loading && !overview) {
    return (
      <div>
        <SectionHeader title="Revenue" description="Loading revenue data..." />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
          {[1,2,3,4].map(i => <div key={i} className="admin-card" style={{ height: '100px' }}><div className="admin-skeleton" style={{ width: '60%', height: '16px', marginBottom: '8px' }} /><div className="admin-skeleton" style={{ width: '40%', height: '28px' }} /></div>)}
        </div>
      </div>
    );
  }

  return (
    <div>
      <SectionHeader
        title="Revenue"
        description="Financial overview and subscription metrics"
        actions={
          <div className="admin-btn-group">
            {[7, 30, 90].map(d => (
              <button key={d} className={`admin-btn admin-btn--sm ${period === d ? 'admin-btn--primary' : 'admin-btn--secondary'}`} onClick={() => setPeriod(d)}>
                {d}d
              </button>
            ))}
            <button className="admin-btn admin-btn--sm admin-btn--secondary" onClick={() => { loadOverview(); loadTrend(); loadInvoices(); }}>
              <i className="fa-solid fa-arrows-rotate" />
            </button>
          </div>
        }
      />

      {/* Billing Filter */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center' }}>
        <span style={{ fontSize: '12px', color: 'var(--admin-text-muted)', marginRight: '4px' }}>Billing:</span>
        {['all', 'monthly', 'yearly'].map(f => (
          <button key={f} className={`admin-btn admin-btn--sm ${billingFilter === f ? 'admin-btn--primary' : 'admin-btn--secondary'}`} onClick={() => setBillingFilter(f)}>
            {f === 'all' ? 'All' : f === 'monthly' ? 'Monthly Only' : 'Yearly Only'}
          </button>
        ))}
      </div>

      {/* Stats Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {billingFilter === 'all' && (
          <>
            <StatCard label="MRR (Monthly)" value={`$${overview?.mrr || '0'}`} icon="fa-solid fa-calendar-day" variant="primary" />
            <StatCard label="Net MRR (Monthly)" value={`$${overview?.netMRR || '0'}`} icon="fa-solid fa-calendar-day" />
            <StatCard label="Effective MRR" value={`$${overview?.totalMRR || '0'}`} icon="fa-solid fa-chart-line" />
            <StatCard label="Net Effective MRR" value={`$${overview?.netTotalMRR || '0'}`} icon="fa-solid fa-chart-line" />
            <StatCard label="ARR (Yearly)" value={`$${overview?.arr || '0'}`} icon="fa-solid fa-calendar" />
            <StatCard label="Net ARR (Yearly)" value={`$${overview?.netARR || '0'}`} icon="fa-solid fa-calendar" />
            <StatCard label="Effective ARR" value={`$${overview?.totalARR || '0'}`} icon="fa-solid fa-chart-bar" />
            <StatCard label="Net Effective ARR" value={`$${overview?.netTotalARR || '0'}`} icon="fa-solid fa-chart-bar" />
          </>
        )}
        {billingFilter === 'monthly' && (
          <>
            <StatCard label="Monthly MRR" value={`$${overview?.mrr || '0'}`} icon="fa-solid fa-calendar-day" variant="primary" />
            <StatCard label="Net Monthly MRR" value={`$${overview?.netMRR || '0'}`} icon="fa-solid fa-calendar-day" />
            <StatCard label="Monthly Subs" value={overview?.monthlySubsCount || 0} icon="fa-solid fa-users" />
          </>
        )}
        {billingFilter === 'yearly' && (
          <>
            <StatCard label="Yearly ARR" value={`$${overview?.arr || '0'}`} icon="fa-solid fa-calendar" variant="primary" />
            <StatCard label="Net Yearly ARR" value={`$${overview?.netARR || '0'}`} icon="fa-solid fa-calendar" />
            <StatCard label="Yearly Subs" value={overview?.yearlySubsCount || 0} icon="fa-solid fa-users" />
            <StatCard label="Yearly as MRR" value={`$${overview?.arr ? (parseFloat(overview.arr) / 12).toFixed(2) : '0'}`} icon="fa-solid fa-chart-line" />
            <StatCard label="Effective ARR" value={`$${overview?.totalARR || '0'}`} icon="fa-solid fa-chart-bar" />
            <StatCard label="Net Effective ARR" value={`$${overview?.netTotalARR || '0'}`} icon="fa-solid fa-chart-bar" />
          </>
        )}
        <StatCard label="Active Subs" value={overview?.activeSubscriptions || 0} icon="fa-solid fa-circle-check" />
        <StatCard label="Trialing" value={overview?.trialingSubscriptions || 0} icon="fa-solid fa-hourglass-half" />
        <StatCard label={`New (${period}d)`} value={overview?.newSubscriptions || 0} icon="fa-solid fa-user-plus" variant="primary" />
        <StatCard label="Churn Rate" value={`${overview?.churnRate || 0}%`} icon="fa-solid fa-user-minus" variant={overview?.churnRate > 5 ? 'danger' : 'default'} />
        <StatCard label="ARPU" value={`$${overview?.arpu || '0'}`} icon="fa-solid fa-dollar-sign" />
      </div>

      {/* Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '20px' }}>
        {/* Revenue Trend */}
        <div className="admin-card" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: 'var(--admin-text-primary)' }}>Revenue Trend</h3>
            <div className="admin-btn-group">
              {[6, 12].map(m => (
                <button key={m} className={`admin-btn admin-btn--sm ${trendMonths === m ? 'admin-btn--primary' : 'admin-btn--ghost'}`} onClick={() => setTrendMonths(m)} style={{ fontSize: '11px', padding: '3px 8px' }}>
                  {m}mo
                </button>
              ))}
            </div>
          </div>
          {trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={trend}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1aad9f" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#1aad9f" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
                <XAxis dataKey="label" tick={{ fill: 'var(--admin-text-muted)', fontSize: 11 }} axisLine={{ stroke: 'var(--admin-border)' }} />
                <YAxis tick={{ fill: 'var(--admin-text-muted)', fontSize: 11 }} axisLine={{ stroke: 'var(--admin-border)' }} tickFormatter={v => `$${v}`} />
                <Tooltip
                  contentStyle={{ background: 'var(--admin-card)', border: '1px solid var(--admin-border)', borderRadius: '8px', fontSize: '12px' }}
                  labelStyle={{ color: 'var(--admin-text-secondary)' }}
                  formatter={(value) => [`$${value.toFixed(2)}`, 'Revenue']}
                />
                <Area type="monotone" dataKey="revenue" stroke="#1aad9f" strokeWidth={2} fill="url(#revenueGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--admin-text-muted)' }}>No data</div>
          )}
        </div>

        {/* Plan Distribution */}
        <div className="admin-card" style={{ padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: 600, color: 'var(--admin-text-primary)' }}>Revenue by Plan</h3>
          {planData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={planData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2} dataKey="value">
                    {planData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: 'var(--admin-card)', border: '1px solid var(--admin-border)', borderRadius: '8px', fontSize: '12px' }}
                    formatter={(value) => [`$${value}/mo`, 'MRR']}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ marginTop: '8px' }}>
                {planData.map((plan, i) => (
                  <div key={plan.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: '12px' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS[i % COLORS.length], display: 'inline-block' }} />
                      <span style={{ color: 'var(--admin-text-secondary)' }}>{plan.name}</span>
                    </span>
                    <span style={{ color: 'var(--admin-text-primary)', fontWeight: 500 }}>${plan.value}/mo ({plan.count})</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--admin-text-muted)' }}>No plans</div>
          )}
        </div>
      </div>

      {/* Recent Invoices */}
      <div className="admin-card">
        <div style={{ padding: '16px 16px 0', marginBottom: '8px' }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: 'var(--admin-text-primary)' }}>Recent Invoices</h3>
        </div>
        <DataTable columns={invoiceColumns} data={invoices} emptyMessage="No invoices found" emptyIcon="fa-solid fa-file-invoice-dollar" />
      </div>
    </div>
  );
}

export default AdminRevenue;
